import { useSelector } from "react-redux";
import { useDealEligibleItems } from "../../discounts/hooks/useDealEligibleItems";
import {
  appliedDiscountCodes,
  dealsForDiscountIds,
  pairRewardsWithDeals,
  shouldShowRewardSection,
} from "../rewards";
import type { RewardRow } from "../rewards";
import { selectCustomerLoyaltyInfo, selectSaleData } from "../selectors";

export interface LoyaltyRewardsInput {
  /** The deals sheet's visible order — the rows are narrowed to these. */
  discountIds?: (string | number)[];
  /** Everything the sheet knows about, as its map or as a list. */
  uniqueDeals?: Record<string, any> | any[];
  /** The `discountId`s the sale/customer/offers response returned — see `isRewardOfferEligible`. */
  offerDiscountIds?: Set<string>;
}

export interface LoyaltyRewards {
  rows: RewardRow[];
  /** False when the programme does not work this way, the customer is not enrolled, or there are no rows. */
  visible: boolean;
  customerLoyaltyInfo: any;
  billDiscountList: any[];
  redeemablePoints: number | undefined;
}

/**
 * Everything the milestone-reward rows need, in one read.
 *
 * The view that consumes this holds no loyalty state and does no matching — `../rewards` decides
 * which milestones have a deal, which are still on screen, and whether the section belongs at
 * all; this hook only supplies the store's answers to those questions.
 *
 * ⚠️ `billDiscountList` comes back too, because a row's redeemed state is read off the SALE, not
 * off local state — see `pairRewardsWithDeals`.
 */
export function useLoyaltyRewards({
  discountIds = [],
  uniqueDeals = {},
  offerDiscountIds,
}: LoyaltyRewardsInput): LoyaltyRewards {
  const customerLoyaltyInfo = useSelector(selectCustomerLoyaltyInfo) || {};
  const saleData = useSelector(selectSaleData);
  const getEligibleItems = useDealEligibleItems();

  const billDiscountList = saleData?.billDiscountList || [];
  const rows = pairRewardsWithDeals({
    milestones: customerLoyaltyInfo?.milestones,
    deals: dealsForDiscountIds(uniqueDeals, discountIds),
    appliedCodes: appliedDiscountCodes(billDiscountList),
    offerDiscountIds,
    getEligibleItems,
  });

  return {
    rows,
    visible: shouldShowRewardSection(customerLoyaltyInfo, rows),
    customerLoyaltyInfo,
    billDiscountList,
    redeemablePoints: customerLoyaltyInfo?.redeemablePoints,
  };
}
